import * as THREE from 'three'
import {
  defaultAnchorSpec,
  PoseSmoother,
  solveFaceAnchor,
  type AnchorSpec,
  type CameraModel,
  type FaceAnchorResult,
  type FaceFrame,
  type POSE_SMOOTHER_PRESETS,
  type PoseSmootherConfig,
} from '@ar-suite/core'
import { applyPose, ARScene, applyMaterialOverrides, disposeObject, FaceOccluder } from '@ar-suite/render-web'

export interface FaceAnchorModuleOptions {
  spec?: AnchorSpec
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
}

/**
 * Generic module for rigid face-anchored wearables: nose pins, bindis, maang tikkas,
 * hats, headphones — anything that is `solveFaceAnchor(origin) → smoother → three.js`.
 * The anchor origin comes entirely from the product's anchor.json, so one instance
 * serves every such category; new categories of this shape need no engine changes.
 */
export class FaceAnchorModule {
  readonly root = new THREE.Group()
  readonly smoother: PoseSmoother
  private _spec: AnchorSpec
  private model: THREE.Object3D | null = null
  private result: FaceAnchorResult | null = null
  private occluder: FaceOccluder | null = null

  constructor(readonly scene: ARScene, options: FaceAnchorModuleOptions = {}) {
    this._spec = options.spec ?? defaultAnchorSpec('bindi')
    this.smoother = new PoseSmoother(options.smoothing ?? 'balanced')
    this.root.name = 'face-prop'
    this.root.visible = false
    scene.content.add(this.root)
  }

  get spec(): AnchorSpec {
    return this._spec
  }
  get lastResult(): FaceAnchorResult | null {
    return this.result
  }
  get hasModel(): boolean {
    return this.model !== null
  }
  set visible(v: boolean) {
    this.root.visible = v
  }

  attachOccluder(occluder: FaceOccluder): void {
    this.occluder = occluder
    occluder.setSpec(this._spec.occluder)
  }

  setSpec(spec: AnchorSpec): void {
    this._spec = spec
    this.occluder?.setSpec(spec.occluder)
  }

  patchSpec(patch: Partial<AnchorSpec>): void {
    Object.assign(this._spec, patch)
    this.setSpec(this._spec)
  }

  setSmoothing(cfg: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this.smoother.configure(cfg)
  }

  setModel(model: THREE.Object3D | null): void {
    if (this.model) {
      this.root.remove(this.model)
      disposeObject(this.model)
    }
    this.model = model
    if (model) this.root.add(model)
  }

  applyVariantMaterials(overrides: Record<string, { color?: string; metalness?: number; roughness?: number; opacity?: number }>): void {
    if (this.model) applyMaterialOverrides(this.model, overrides)
  }

  update(frame: FaceFrame, cam: CameraModel, tMs: number): FaceAnchorResult {
    this.result = solveFaceAnchor({ frame, camera: cam, spec: this._spec }, this.result ?? undefined)
    const smoothed = this.smoother.update(this.result.pose, tMs)
    applyPose(this.root, smoothed)
    this.root.visible = true
    return this.result
  }

  onTrackingLost(): void {
    this.root.visible = false
    this.smoother.reset()
  }

  dispose(): void {
    this.setModel(null)
    this.scene.content.remove(this.root)
  }
}
